import React from 'react'
import type { LinkableTextFrame } from '@/lib/text-linking/types/linkable-text-frame'

import type { PortControlProps, PortEdge, PortFocusRequest } from '../types/port-control'
import type { TextLinkingModeState } from '../types/text-linking-layer-props'

function portFocusKey(edge: PortEdge, frameId: string): string {
  return `${edge}:${frameId}`
}

/**
 * Keyboard focus for the layer's ports: which port shows its ring, the element registry focus is
 * moved through, and the post-render handoff that keeps focus on a sensible port when activating
 * one unmounts the element that held it (see `PortFocusRequest`).
 */
export function usePortFocus({
  portFrames,
  linking,
  onCancelLinking,
}: {
  portFrames: LinkableTextFrame[]
  linking: TextLinkingModeState | null
  onCancelLinking?: () => void
}): {
  buildPortControl: (
    edge: PortEdge,
    frameId: string,
    label: string,
    onActivateByKeyboard: () => void,
    tabbable?: boolean
  ) => PortControlProps
  /** Queue a focus move for after the next render (see `PortFocusRequest`). */
  requestFocus: (request: PortFocusRequest) => void
} {
  const linkingActive = linking != null
  const [focusedPortKey, setFocusedPortKey] = React.useState<string | null>(null)
  // A ref, not state: every port activation already changes parent state (`textLinking`, or the
  // chains rebuilt after a link/unlink), so the re-render this focus move waits on is guaranteed
  // without one more of our own.
  const pendingFocusRef = React.useRef<PortFocusRequest | null>(null)
  const portRefs = React.useRef(new Map<string, SVGRectElement>())

  const requestFocus = (request: PortFocusRequest): void => {
    pendingFocusRef.current = request
  }

  const cancelLinkingByKeyboard = (): void => {
    const sourceId = linking?.sourceId

    onCancelLinking?.()
    // The source's "+" is hidden while it is the source and comes back on cancel — land there, so
    // Escape returns the user to exactly where the gesture began.
    if (sourceId) {
      requestFocus({ kind: 'port', edge: 'out', frameIds: [sourceId] })
    }
  }

  function buildPortControl(
    edge: PortEdge,
    frameId: string,
    label: string,
    onActivateByKeyboard: () => void,
    tabbable = true
  ): PortControlProps {
    const key = portFocusKey(edge, frameId)

    return {
      label,
      tabbable,
      focused: focusedPortKey === key,
      onActivateByKeyboard,
      onCancelByKeyboard: linkingActive ? cancelLinkingByKeyboard : undefined,
      onFocusChange: (focused) => {
        setFocusedPortKey((prev) => {
          if (focused) return key

          return prev === key ? null : prev
        })
      },
      registerRef: (el) => {
        // Block body with no return value on purpose: React 19 reads a ref callback's return as a
        // cleanup function and rejects anything else at the type level.
        if (el) {
          portRefs.current.set(key, el)
        } else {
          portRefs.current.delete(key)
        }
      },
    }
  }

  // Apply a queued focus move once the render it depends on has committed. `useLayoutEffect` (not
  // `useEffect`) so focus lands before paint and the ring never flashes on the old element.
  //
  // No dependency array on purpose. The render that makes the focus target exist is exactly the one
  // that follows the activation, and `portFrames` is rebuilt from scratch each render out of an
  // imperative manager ref — it has no stable identity to key on, and memoizing it is precisely
  // what the overlay's mount comment (center-canvas/components/screen-space-layers.tsx) warns draws
  // stale ports. The early return keeps this a no-op on every unrelated render.
  React.useLayoutEffect(() => {
    const request = pendingFocusRef.current

    if (!request) return
    // Cleared before the focus call, so a request can never get stuck and re-fire later.
    pendingFocusRef.current = null

    const edge = request.kind === 'first-target' ? 'in' : request.edge
    const candidateIds =
      request.kind === 'first-target'
        ? portFrames.filter((f) => f.id !== linking?.sourceId).map((f) => f.id)
        : request.frameIds

    candidateIds
      .map((frameId) => portRefs.current.get(portFocusKey(edge, frameId)))
      .find((el) => el != null)
      ?.focus()
  })

  return { buildPortControl, requestFocus }
}
